import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreatePotRequest,
  FetchPotsRequest,
  FetchPotsResponse,
  potsService,
  useToast,
  Pot,
} from '@aslanpay/portal-common-lib';

/**
 * Query params for a company's points pot: its single ACTIVE COMPANY pot.
 *
 * @param companyId the company whose pot to fetch
 * @returns the pots query params for that company's active pot
 */
export const companyPotQueryParams = (companyId?: string): FetchPotsRequest => ({
  companyIds: [companyId || ''],
  relatedType: ['COMPANY'],
  status: ['ACTIVE'],
  page: 0,
  size: 1,
});

const useFetchPots = (
  {
    ids,
    companyIds,
    relatedType,
    relatedIds,
    name,
    status,
    sortBy,
    sort,
    page,
    size,
  }: FetchPotsRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<FetchPotsResponse, Error>({
    queryKey: [
      'fetchPots',
      {
        ids,
        companyIds,
        relatedType,
        relatedIds,
        name,
        status,
        sortBy,
        sort,
        page,
        size,
      },
    ],
    queryFn: () =>
      potsService.fetchPots({
        ids,
        companyIds,
        relatedType,
        relatedIds,
        name,
        status,
        sortBy,
        sort,
        page,
        size,
      }),
    enabled,
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, we failed to fetch points data. Please try again.',
      });
    },
  });
};

const useCreatePot = () => {
  const queryClient = useQueryClient();

  return useMutation<Pot, Error, CreatePotRequest>({
    mutationFn: (body: CreatePotRequest) => {
      if (!body.companyId) throw new Error('companyId is required');
      return potsService.createPot(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
    },
  });
};

const useArchivePot = () => {
  const queryClient = useQueryClient();

  return useMutation<Pot, Error, string>(
    (potId: string) => {
      if (!potId) throw new Error('potId is required');
      return potsService.archivePot(potId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
      },
    }
  );
};

const usePots = {
  useFetchPots,
  useCreatePot,
  useArchivePot,
};

export default usePots;
